import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccessLevel } from '../../../shared/schemas';
import { apiFetch, selectedGuildId, setCsrfToken } from './api';

export interface PanelUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  readonly level: AccessLevel;
  readonly levelLabel: string;
  /**
   * true = tài khoản này nằm trong `BOT_OWNER_IDS`.
   *
   * Chỉ dùng để ẩn mục menu — KHÔNG phải để bảo vệ. Máy chủ kiểm lại ở mọi
   * endpoint của nhóm khoá dùng chung (`requireBotOwner`).
   */
  readonly isBotOwner?: boolean;
  /**
   * Cấp quyền ở TỪNG server — `{ [guildId]: 'admin' | 'mod' }`.
   *
   * `level` ở trên là cấp cao nhất trên mọi server, nên nó KHÔNG trả lời được
   * câu hỏi duy nhất giao diện cần hỏi: ở server đang mở thì người này là gì.
   * Xem `isAdmin` bên dưới.
   */
  readonly guilds?: Readonly<Record<string, AccessLevel>>;
}

interface MeResponse {
  authenticated: boolean;
  user?: PanelUser;
  csrfToken?: string;
  sessionExpiresAt?: number;
  guildName?: string;
  loginUrl?: string;
}

interface AuthValue {
  readonly user: PanelUser | null;
  readonly guildName: string;
  readonly loading: boolean;
  /**
   * true = Quản trị viên **của server đang mở**. Dùng để ẩn nút — KHÔNG phải để
   * bảo vệ.
   *
   * "Của server đang mở" là phần dễ bỏ sót, và bỏ sót nó thì người admin ở
   * server A / mod ở server B nhìn thấy đủ nút quản trị viên khi đang xem B —
   * mỗi cú bấm là một lỗi 403 không giải thích được. Xem `PanelUser.guilds`.
   */
  readonly isAdmin: boolean;
  /** true = chủ bot (`BOT_OWNER_IDS`). Cũng chỉ để ẩn mục menu. */
  readonly isBotOwner: boolean;
  readonly sessionExpiresAt: number | null;
  /**
   * Khai bằng cú pháp thuộc tính, không phải phương thức: giá trị thật là một
   * hàm mũi tên đóng kín, nên nó không phụ thuộc `this` — mà cú pháp phương
   * thức lại nói ngược lại, khiến mọi chỗ destructuring `logout` bị cảnh báo
   * về `this` không tồn tại.
   */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiFetch<MeResponse>('/api/auth/me'),
    // Hỏi lại mỗi 5 phút. Máy chủ hủy phiên ngay khi vai trò Discord thay đổi,
    // nên không có bước này thì giao diện vẫn hiện đầy đủ nút cho một người vừa
    // mất quyền — họ bấm và nhận lỗi 403 mà không hiểu vì sao.
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const value = useMemo<AuthValue>(() => {
    const token = data?.csrfToken ?? '';
    setCsrfToken(token);

    const user = data?.authenticated === true ? (data.user ?? null) : null;

    /**
     * Đọc `selectedGuildId()` thẳng từ module thay vì nhận qua props: nguồn thật
     * của nó là localStorage, và mọi lượt ĐỔI server đều nạp lại cả trang
     * (`Layout` gọi `window.location.reload()`), nên không có khoảnh khắc nào
     * giá trị này cũ đi mà giao diện còn sống để hiển thị sai.
     *
     * Chưa chọn server (đang ở trang chủ) → chuỗi rỗng → không khớp khoá nào →
     * `false`. Đó là câu trả lời đúng: chưa có server thì chưa có quyền nào để
     * mà nói tới.
     */
    const levelHere = user?.guilds?.[selectedGuildId()] ?? null;

    return {
      user,
      guildName: data?.guildName ?? '',
      loading: isLoading,
      isAdmin: levelHere === 'admin',
      isBotOwner: user?.isBotOwner === true,
      sessionExpiresAt: data?.sessionExpiresAt ?? null,
      logout: async () => {
        await apiFetch('/api/auth/logout', { method: 'POST' });
        // Xoá sạch cache trước khi tải lại: giữ lại thì trong khoảnh khắc trước
        // khi trang mới lên, dữ liệu của phiên vừa đóng vẫn còn hiện trên màn hình.
        queryClient.clear();
        window.location.href = '/';
      },
    };
  }, [data, isLoading, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth phải nằm trong <AuthProvider>');
  return value;
}
